import { classes } from "portal/theme/theme";
import {
  DateRange,
  DateTimeRangePicker,
  type DateTimeRangePickerProps,
  MultiInputDateTimeRangeField,
} from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { isSameDay, snapToDayEnd } from "portal/utils/dates";
import { useShadowValue } from "portal/utils/hooks/useShadowValue";
import { useTheme } from "@mui/system";
import { useTranslation } from "react-i18next";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import React, { FunctionComponent } from "react";

const _CarbonDateTimeRangePicker: FunctionComponent<
  Omit<DateTimeRangePickerProps, "onChange"> & {
    openEnded?: boolean;
    onChange?: (val: DateRange<DateTime> | undefined) => void;
  }
> = ({
  slots,
  slotProps,
  value: externalValue,
  readOnly,
  onChange,
  openEnded = false,
  ...props
}) => {
  const { t } = useTranslation();
  const muiTheme = useTheme();

  const [value, setValue] = useShadowValue<DateRange<DateTime> | undefined>(
    externalValue
  );

  return (
    <DateTimeRangePicker
      readOnly={readOnly}
      localeText={{
        start: t("components.DateRangePicker.startDate"),
        end: t("components.DateRangePicker.endDate"),
        cancelButtonLabel: t("components.DateRangePicker.cancel"),
        clearButtonLabel: t("components.DateRangePicker.clear"),
        okButtonLabel: t("components.DateRangePicker.confirm"),
      }}
      slots={{
        field: MultiInputDateTimeRangeField,
        ...slots,
      }}
      value={value ?? externalValue}
      onChange={([start, end]) => {
        let safeEnd = end;
        if (value && !value[1] && end) {
          const endOfDay = snapToDayEnd(end);
          const isToday = isSameDay(end);
          safeEnd = isToday ? DateTime.now() : endOfDay;
        }
        const newRange: DateRange<DateTime> = [start, safeEnd];
        setValue(newRange);

        if (!onChange) {
          return;
        }
        if (!start && !safeEnd) {
          onChange(undefined);
          return;
        }
        if (
          (!openEnded && (!start || !safeEnd)) ||
          (start && !start.isValid) ||
          (safeEnd && !safeEnd.isValid)
        ) {
          return;
        }
        onChange(newRange);
      }}
      slotProps={{
        field: {
          className: classes(
            "group flex gap-2 [&_.MuiMultiInputDateTimeRangeField-separator]:hidden",
            props.className,
            {
              "Mui-readOnly": readOnly,
            }
          ),
        },
        previousIconButton: {
          style: {
            color: muiTheme.palette.text.primary,
          },
          // MUI has a race that is more obvious when you have quicker clicks like Macbook's 'tap to click' instead of a
          // standard click. It results in closing out the window when you're just trying to go next/prev.
          // These 'onMouseDown' overrides mitigate that
          onMouseDown: (e) => e.preventDefault(),
        },
        nextIconButton: {
          style: {
            color: muiTheme.palette.text.primary,
          },
          onMouseDown: (e) => e.preventDefault(),
        },
        ...slotProps,
      }}
      {...props}
    />
  );
};

export const CarbonDateTimeRangePicker = withErrorBoundary(
  {
    i18nKey: "components.DateRangePicker.error",
    small: true,
  },
  _CarbonDateTimeRangePicker
);
